import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { NavIcon } from '@l2u/ui/components/NavIcon.tsx';
import { SubmissionStatusBadge } from '@l2u/ui/components/StatusBadge.tsx';
import { expiryState } from '@l2u/ui/lib/expiry.ts';
import { effectiveDocStatus } from '@l2u/ui/lib/submissionStatus.ts';
import { instanceOptionLabel } from '@l2u/ui/lib/instanceOption.ts';
import { complianceDocName } from '@l2u/ui/lib/complianceDocName.ts';
import type { ComplianceDocRow } from '../lib/companyDetailTabs.ts';
import type { AdminSubmission, CompanyDocument, Question } from '@l2u/ui/lib/types.ts';
import { awaitingAdminReply } from './awaitingReply.ts';
import { CommentModal } from './CommentModal.tsx';
import { FileAnalysisButton } from './DocumentAnalysis.tsx';
import { adminDocumentsTabApprovals } from './documentApprovals.ts';
import { documentRowData, showReverifyButtons } from './documentRow.ts';
import { FileExpiryControl } from './FileExpiryControl.tsx';
import { FileHistoryModal } from './FileHistoryModal.tsx';
import { FilePreviewModal } from './FilePreviewModal.tsx';
import { NotCompliantModal } from './NotCompliantModal.tsx';
import { useReviewMutation } from './useReviewMutation.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { canWrite } from '../lib/adminAccess.ts';
import { showExpiryButton } from '../lib/expiryButton.ts';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// A scannable table of document_upload questions (the Compliance Documents
// tab). This table is verify-only for approved docs: it lists them and their
// status, with Compliant / Not compliant / N/A happening inline in the
// questionnaire tabs (SubmissionRow) instead. The exception is renewal rows
// awaiting re-verification (vendor replaced an expired approved document),
// which carry the same three review buttons as the questionnaire rows. Other
// question types keep the SubmissionRow layout (handled by the caller).
export function DocumentReviewTable({
  rows,
  subByQ,
  companyId,
  documents,
  documentName,
  questionById,
}: {
  rows: ComplianceDocRow[];
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  documents: CompanyDocument[];
  documentName: (id: string) => string;
  questionById: Map<string, Question>;
}) {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2">Document</th>
            <th className="px-4 py-2">Category</th>
            <th className="px-4 py-2">Uploaded</th>
            <th className="px-4 py-2">Expiry</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ question, categoryName }) => (
            <DocumentReviewRow
              key={question.id}
              question={question}
              categoryName={categoryName}
              submission={subByQ.get(question.id)}
              companyId={companyId}
              documents={documents}
              documentName={documentName}
              instanceOption={instanceOptionLabel(question, questionById)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentReviewRow({
  question,
  categoryName,
  submission,
  companyId,
  documents,
  documentName,
  instanceOption,
}: {
  question: Question;
  categoryName: string;
  submission: AdminSubmission | undefined;
  companyId: string;
  documents: CompanyDocument[];
  documentName: (id: string) => string;
  instanceOption: string | null;
}) {
  const docName = complianceDocName(question);
  const [commentOpen, setCommentOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const reviewMutation = useReviewMutation(companyId, submission?.id, () => setRejectOpen(false));
  // Read locally, like SubmissionRow: the role decides whether the verdict
  // buttons exist at all. Preview and history stay open -- they are reads.
  const { user } = useAdminAuth();
  const mayWrite = canWrite(user?.role);
  // Renewal awaiting re-verification: the vendor replaced an expired approved
  // document. Only these rows carry review buttons — approved rows stay
  // verify-only (PR #149). Buttons stay editable until the verdict is notified
  // to the vendor, so an accidental misclick can still be toggled/reset; see
  // showReverifyButtons.
  const needsReverify = showReverifyButtons(submission);

  // Effective (draft-overlaid) verdict, so the active button reflects an
  // unpublished draft and clicking it again resets to pending.
  const isApproved = submission?.status === 'approved';
  const isRejected = submission?.status === 'rejected';
  const isNa = submission?.status === 'not_applicable';
  const busy = reviewMutation.isPending;
  const dec = reviewMutation.variables?.decision;
  const loadingFor = (target: 'approve' | 'reject' | 'na', active: boolean) =>
    busy && (dec === target || (dec === 'reset' && active));

  const row = documentRowData(submission);
  const files = submission?.submission_files ?? [];
  const awaitingReply = awaitingAdminReply(submission, question.include_in_compliance_documents);
  // The thread's approval cut points, judged on the LIVE status (both modals).
  const approvals = adminDocumentsTabApprovals(question, submission);
  // An approved document whose expiry has lapsed is no longer compliant — show
  // it as "Not Compliant" (display-only; see effectiveDocStatus).
  const expired = row.kind === 'file' && expiryState({ expires_at: row.expiresAt }) === 'expired';
  // Live files with something for the Expiry cell to show: a date, or (for a
  // writer) an Add expiry button. `files` is live-only, so an edit here always
  // targets the current upload, never a superseded version.
  const expiryFiles = files.filter(
    (f) =>
      f.expires_at != null ||
      showExpiryButton({
        expiryEditable: mayWrite,
        requiresExpiry: question.requires_expiry,
        expiresAt: f.expires_at,
      }),
  );

  const uploaded =
    row.kind === 'file'
      ? formatDate(row.uploadedAt)
      : row.kind === 'adopted' && row.uploadedAt
        ? formatDate(row.uploadedAt)
        : '—';

  return (
    <>
      <tr className="border-t border-slate-100 align-top">
        <td className="px-4 py-3">
          <div className="font-semibold text-slate-900">
            {docName}
            {instanceOption && (
              <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
                For: {instanceOption}
              </span>
            )}
          </div>
          {submission?.reviewer_comment && (
            <div
              className="mt-1 flex items-start gap-1 text-xs text-orange-700"
              title={submission.reviewer_comment}
            >
              <NavIcon name="message-square" size={12} />
              <span>{submission.reviewer_comment}</span>
            </div>
          )}
          {row.kind === 'adopted' && (
            <div className="mt-0.5 text-xs text-slate-500">
              Adopted: {row.documentId ? documentName(row.documentId) : 'document'}
            </div>
          )}
          {row.kind === 'awaiting' && (
            <div className="mt-0.5 text-xs italic text-slate-400">Awaiting document</div>
          )}
        </td>
        <td className="px-4 py-3 text-slate-600">{categoryName}</td>
        <td className="px-4 py-3 text-slate-600">{uploaded}</td>
        <td className="px-4 py-3">
          {submission && expiryFiles.length > 0 ? (
            <div className="flex flex-col items-start gap-1">
              {expiryFiles.map((f) => (
                <div key={f.id} className="flex flex-wrap items-center gap-1.5">
                  {files.length > 1 && (
                    <span
                      className="max-w-[10rem] truncate text-xs text-slate-500"
                      title={f.file_name}
                    >
                      {f.file_name}
                    </span>
                  )}
                  <FileExpiryControl
                    companyId={companyId}
                    submissionId={submission.id}
                    file={f}
                    requiresExpiry={question.requires_expiry}
                    editable={mayWrite}
                  />
                </div>
              ))}
            </div>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          {/* Compliance-exempt rows have no status to show: nobody reviews them,
              so the stored status stays 'pending' and the badge contradicted the
              "no review needed" label in this row's own actions cell. Same muted
              dash the expiry cell uses for "nothing to show here". */}
          {question.exclude_from_compliance ? (
            <span className="text-slate-400">—</span>
          ) : (
            <SubmissionStatusBadge
              status={effectiveDocStatus(submission?.status ?? 'pending', expired)}
            />
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-1">
            {mayWrite && needsReverify && submission && (
              <>
                <Btn
                  size="sm"
                  variant={isApproved ? 'success' : 'secondary'}
                  aria-pressed={isApproved}
                  aria-label={`Mark "${docName}" compliant`}
                  onClick={() =>
                    reviewMutation.mutate({ decision: isApproved ? 'reset' : 'approve' })
                  }
                  loading={loadingFor('approve', isApproved)}
                >
                  <NavIcon name="check-circle" size={16} />
                  Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={isRejected ? 'danger' : 'secondary'}
                  aria-pressed={isRejected}
                  aria-label={`Mark "${docName}" not compliant`}
                  onClick={() =>
                    isRejected ? reviewMutation.mutate({ decision: 'reset' }) : setRejectOpen(true)
                  }
                  loading={loadingFor('reject', isRejected)}
                >
                  <NavIcon name="x-circle" size={16} />
                  Not Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={isNa ? 'warning' : 'secondary'}
                  aria-pressed={isNa}
                  aria-label={`Mark "${docName}" not applicable`}
                  onClick={() => reviewMutation.mutate({ decision: isNa ? 'reset' : 'na' })}
                  loading={loadingFor('na', isNa)}
                >
                  N/A
                </Btn>
              </>
            )}
            <Btn
              size="sm"
              variant="secondary"
              aria-label="Document history"
              title="History"
              disabled={!submission}
              onClick={() => setHistoryOpen(true)}
            >
              <NavIcon name="clock" size={16} />
            </Btn>
            <Btn
              size="sm"
              variant="secondary"
              aria-label="View document"
              title="View"
              disabled={row.kind !== 'file'}
              onClick={() => setPreviewOpen(true)}
            >
              <NavIcon name="eye" size={16} />
            </Btn>
            {/* Advisory AI verdict for the document under review, behind a
                button. `files` is live-only (GET /companies/:id/submissions
                filters superseded_at is null), so this is the verdict for the
                CURRENT upload — a renewal opens its own verdict, not the
                superseded version's. Shown on every row that has an upload,
                whether or not the AI ran — the modal says when there is nothing.
                The files.length gate is the scope: with nothing uploaded there is
                no document for the AI to have checked, so no button. */}
            {submission && files.length > 0 && (
              <FileAnalysisButton
                companyId={companyId}
                submissionId={submission.id}
                files={files}
                docLabel={docName}
              />
            )}
            {question.exclude_from_compliance ? (
              <span className="ml-1 text-xs text-slate-500">
                Not part of compliance — no review needed.
              </span>
            ) : !mayWrite ? null : (
              <span className="relative inline-flex">
                <Btn
                  size="sm"
                  variant="secondary"
                  aria-label={
                    awaitingReply
                      ? `Vendor replied — awaiting your reply for "${docName}"`
                      : `Add comment for "${docName}"`
                  }
                  title={awaitingReply ? 'Vendor replied — awaiting your reply' : 'Comment'}
                  onClick={() => setCommentOpen(true)}
                >
                  <NavIcon name="message-square" size={16} />
                </Btn>
                {awaitingReply && (
                  <span
                    className="pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                    aria-hidden="true"
                  />
                )}
              </span>
            )}
          </div>
        </td>
      </tr>
      {createPortal(
        <>
          {mayWrite && commentOpen && (
            <CommentModal
              question={question}
              companyId={companyId}
              submissionId={submission?.id}
              submission={submission}
              onClose={() => setCommentOpen(false)}
              scope="documents"
              approvals={approvals}
            />
          )}
          {previewOpen && row.kind === 'file' && submission && (
            <FilePreviewModal
              companyId={companyId}
              submissionId={submission.id}
              files={files}
              startIndex={Math.max(0, files.indexOf(row.primaryFile))}
              onClose={() => setPreviewOpen(false)}
            />
          )}
          {historyOpen && submission && (
            <FileHistoryModal
              companyId={companyId}
              submissionId={submission.id}
              questionLabel={docName}
              approvals={approvals}
              onClose={() => setHistoryOpen(false)}
            />
          )}
          {mayWrite && rejectOpen && (
            <NotCompliantModal
              question={question}
              displayName={docName}
              documents={documents}
              pending={reviewMutation.isPending}
              error={reviewMutation.error}
              onClose={() => setRejectOpen(false)}
              onSubmit={(payload) => reviewMutation.mutate({ decision: 'reject', ...payload })}
            />
          )}
        </>,
        document.body,
      )}
    </>
  );
}
